// src/pages/Visitors/parts/ViewVisitorModal.tsx
//
// Visitor detail view — GET /visitors/:id, with the check-out action
// (PUT /visitors/:id/check-out) available while the visitor is still in.

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import {
  ApiVisitorLog,
  STATUS_META,
  VisitorRecord,
  formatTime,
  formatVisitDate,
  getInitials,
  mapApiVisitor,
} from './types';

interface ViewVisitorModalProps {
  open: boolean;
  visitorId: string | null;
  onClose: () => void;
  /** Called after a successful check-out so the list can refresh. */
  onChanged: () => void;
  onEdit: (visitor: VisitorRecord) => void;
}

const ViewVisitorModal: React.FC<ViewVisitorModalProps> = ({
  open,
  visitorId,
  onClose,
  onChanged,
  onEdit,
}) => {
  const toast = useRef<Toast>(null);
  const [visitor, setVisitor] = useState<VisitorRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open || !visitorId) {
      setVisitor(null);
      setIsClosing(false);
      return;
    }

    let cancelled = false;

    const fetchVisitor = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`api/visitors/${visitorId}`);
        if (cancelled) return;
        if (res.data.status === 'success') {
          setVisitor(mapApiVisitor(res.data.data as ApiVisitorLog));
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Not loaded',
            detail: res.data.message || 'This visitor record could not be loaded.',
            life: 5000,
          });
        }
      } catch (err: any) {
        if (cancelled) return;
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Could not load this visitor record.',
          life: 5000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchVisitor();
    return () => {
      cancelled = true;
    };
  }, [open, visitorId]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (checkingOut) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleCheckOut = async () => {
    if (!visitor || checkingOut) return;
    setCheckingOut(true);
    try {
      const res = await apiClient.put(`api/visitors/${visitor.id}/check-out`);
      if (res.data.status === 'success') {
        setVisitor({
          ...visitor,
          status: 'checked_out',
          checkOutTime: res.data.check_out_time ?? new Date().toISOString(),
        });
        toast.current?.show({
          severity: 'success',
          summary: 'Checked out',
          detail: `${visitor.visitorName} has left.`,
          life: 4000,
        });
        onChanged();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The visitor could not be checked out.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while checking out.',
        life: 5000,
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const sectionCardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 16,
    padding: 20,
    border: '1px solid rgba(59, 130, 246, 0.1)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  };

  const Row: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({
    icon,
    label,
    value,
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(248, 250, 252, 0.9)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.12) 100%)',
          color: '#3b82f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 14 }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#1e293b',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {value || <span style={{ color: '#94a3b8', fontWeight: 500 }}>—</span>}
        </div>
      </div>
    </div>
  );

  const Section: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
    icon,
    title,
    children,
  }) => (
    <div style={sectionCardStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={icon} style={{ fontSize: 14 }} />
        </div>
        <span
          style={{
            color: '#1e3a8a',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );

  const statusMeta = visitor ? STATUS_META[visitor.status] : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0', width: '100%' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          fontSize: 18,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          border: '3px solid white',
          letterSpacing: '0.02em',
        }}
      >
        {loading ? (
          <i className="pi pi-user" style={{ fontSize: 20 }} />
        ) : (
          getInitials(visitor?.visitorName ?? '')
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#1e40af',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {loading ? 'Loading visitor…' : visitor?.visitorName || 'Visitor'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {loading
            ? 'Fetching visit details'
            : visitor?.visitorType
              ? `${visitor.visitorType} · Visit record`
              : 'Visit record'}
        </div>
      </div>
      {statusMeta && !loading && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: statusMeta.color,
            background: statusMeta.bg,
            border: `1px solid ${statusMeta.border}`,
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <i className={statusMeta.icon} style={{ fontSize: 12 }} />
          {statusMeta.label}
        </span>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 140px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!checkingOut}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
        `}</style>

        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '64px 20px',
              gap: 16,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              }}
            >
              <ProgressSpinner
                style={{ width: 28, height: 28 }}
                strokeWidth="4"
                animationDuration=".8s"
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#1e40af', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                Loading visitor
              </div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                Fetching visit details…
              </div>
            </div>
          </div>
        ) : visitor ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Visit timeline */}
            <Section icon="pi pi-clock" title="Visit">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                <Row icon="pi pi-calendar" label="Date" value={formatVisitDate(visitor.visitDate)} />
                <Row
                  icon="pi pi-sign-in"
                  label="Checked in"
                  value={formatTime(visitor.checkInTime)}
                />
                <Row
                  icon="pi pi-sign-out"
                  label="Checked out"
                  value={formatTime(visitor.checkOutTime)}
                />
              </div>
            </Section>

            {/* Purpose */}
            <Section icon="pi pi-bookmark" title="Purpose">
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 14px',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.08) 0%, rgba(59, 130, 246, 0.1) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  color: '#1e40af',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <i className="pi pi-flag" style={{ fontSize: 12 }} />
                {visitor.purpose}
              </div>
              {visitor.purposeDetails ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(248, 250, 252, 0.9)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    fontSize: 14,
                    color: '#475569',
                    lineHeight: 1.55,
                    fontWeight: 500,
                  }}
                >
                  {visitor.purposeDetails}
                </div>
              ) : (
                <div style={{ marginTop: 10, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
                  No additional purpose details recorded.
                </div>
              )}
            </Section>

            {/* People */}
            <Section icon="pi pi-users" title="People">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 10,
                }}
              >
                <Row icon="pi pi-user" label="Meeting" value={visitor.meetPersonName} />
                <Row icon="pi pi-briefcase" label="Role" value={visitor.meetPersonType} />
                <Row icon="pi pi-phone" label="Contact" value={visitor.contactNo} />
              </div>
            </Section>

            {/* Identification */}
            <Section icon="pi pi-id-card" title="Identification">
              <div style={{ marginBottom: visitor.photoUrl ? 14 : 0 }}>
                <Row icon="pi pi-id-card" label="ID proof type" value={visitor.idProofType} />
              </div>
              {visitor.photoUrl ? (
                <a
                  href={visitor.photoUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'inline-block', textDecoration: 'none' }}
                >
                  <div
                    style={{
                      borderRadius: 14,
                      overflow: 'hidden',
                      border: '1.5px solid rgba(59, 130, 246, 0.2)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                      maxWidth: 360,
                      background: '#f8fafc',
                    }}
                  >
                    <img
                      src={visitor.photoUrl}
                      alt="ID proof"
                      style={{
                        width: '100%',
                        display: 'block',
                        maxHeight: 220,
                        objectFit: 'cover',
                      }}
                    />
                    <div
                      style={{
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'rgba(239, 246, 255, 0.9)',
                      }}
                    >
                      <i className="pi pi-external-link" style={{ fontSize: 11 }} />
                      Open full size
                    </div>
                  </div>
                </a>
              ) : (
                <div
                  style={{
                    marginTop: 4,
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: '1.5px dashed #c7d2fe',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    color: '#64748b',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-image" style={{ fontSize: 16, color: '#93c5fd' }} />
                  No ID proof photo was recorded for this visit.
                </div>
              )}
            </Section>

            {/* Recorded by */}
            <Section icon="pi pi-history" title="Recorded by">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 10,
                }}
              >
                <Row
                  icon="pi pi-user-plus"
                  label="Checked in by"
                  value={visitor.checkedInByName}
                />
                <Row
                  icon="pi pi-user-minus"
                  label="Checked out by"
                  value={visitor.checkedOutByName}
                />
              </div>
            </Section>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 10,
                marginTop: 4,
                paddingTop: 4,
              }}
            >
              <GlobalButton
                label="Edit details"
                icon="pi pi-pencil"
                onClick={() => onEdit(visitor)}
                variant="secondary"
                size="medium"
                disabled={checkingOut}
                fullWidth={false}
              />
              {visitor.status === 'checked_in' && (
                <GlobalButton
                  label={checkingOut ? 'Checking out…' : 'Check out'}
                  icon="pi pi-sign-out"
                  onClick={handleCheckOut}
                  variant="primary"
                  size="medium"
                  disabled={checkingOut}
                  loading={checkingOut}
                  fullWidth={false}
                />
              )}
              <GlobalButton
                label="Close"
                onClick={handleClose}
                variant="secondary"
                size="medium"
                disabled={checkingOut}
                fullWidth={false}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '56px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.25)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ fontSize: 22, color: 'white' }} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              Visitor record unavailable
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, maxWidth: 280 }}>
              This visitor record could not be loaded. It may have been removed.
            </div>
            <div style={{ marginTop: 8 }}>
              <GlobalButton
                label="Close"
                onClick={handleClose}
                variant="secondary"
                size="medium"
                fullWidth={false}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ViewVisitorModal;